import { html, nothing, type TemplateResult } from 'lit';

import { LinkifyIt } from 'linkify-it';

import { EMOTE_NAMES } from '../../../generated/emotes';
import type { FlagCode } from '../../../generated/flags';
import { flagSvgPath, resolveFlag } from '../../../services/Flags';

/**
 * Shared inline chat-text renderer — turns a raw message string into lit
 * template parts: plain text, clickable truncated URLs, inline emote images
 * (`-lol-` / `:lol:`) and inline flag images (`-flagbr-` / `:flagbr:`).
 *
 * Extracted from the desktop game-chat so the mobile drawer renders messages
 * identically (players saw raw `:bananas:` there). Consumers must style the
 * emitted classes themselves (Shadow DOM; components are self-contained):
 * `.inline-emote`, `.inline-flag`, `a`, `.sr-only`.
 */

// linkify-it 6 flipped two defaults that were on in 5: `fuzzyLink` (bare
// domains like github.com) and `urlAuth` (without it, http://user:pass@host
// truncates to a bogus http://user link). Both restored for parity.
const linkify = new LinkifyIt({ fuzzyLink: true, urlAuth: true });

/** Fast lookup for inline emote detection (-name- in chat text). */
const EMOTE_SET: ReadonlySet<string> = new Set(EMOTE_NAMES);

/**
 * Matches `-word-` and `:word:` tokens. Dash form is StarWarsMod-compatible
 * inline emote/flag syntax (typed in chat). Colon form is what emote and flag
 * slash-commands produce on the wire (e.g. `/angry` → `:angry:` SAY, `/flagus`
 * → `:flagus:`) — we render both so /s messages with an emote show the image,
 * not the raw `:angry:` text.
 */
const INLINE_TOKEN_RE = /-([\w-]+)-|:([\w-]+):/g;

/** Truncates a web URL to domain-only display: "example.com/…". Works from
 *  linkify's NORMALIZED url rather than the typed text — fuzzy matches like
 *  `example.com/long/path` carry no protocol in the text (and casing like
 *  `HTTPS://` defeated the old prefix test), so they escaped truncation.
 *  Non-web schemes (mailto:) fall back to the typed text untouched. */
function truncateUrl(url: string, fallbackText: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return fallbackText;
    const hasPath = parsed.pathname !== '' && parsed.pathname !== '/';
    const hasExtras = hasPath || parsed.search !== '' || parsed.hash !== '';
    return hasExtras ? `${parsed.host}/…` : parsed.host;
  } catch {
    return fallbackText;
  }
}

/** Flag background-image style helper, shared with game-chat's sender flags */
export function flagStyle(flag: FlagCode): string {
  return `background-image: url('${flagSvgPath(flag)}')`;
}

/**
 * Parses `-name-` and `:name:` tokens in a plain text segment.
 * Emits an inline emote image for known emote names and an inline flag image
 * for `-flag{code}-` / `:flag{code}:` tokens. Unknown tokens pass through as
 * plain text. Compatible with StarWarsMod's chat inline syntax and with the
 * SAY format emote slash-commands produce (`:angry:`, `:flagus:`).
 */
function renderInlineTokens(text: string): (TemplateResult | string)[] {
  if (!text.includes('-') && !text.includes(':')) return [text];

  const parts: (TemplateResult | string)[] = [];
  let lastIndex = 0;
  // Reset global regex state before reuse
  INLINE_TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_TOKEN_RE.exec(text)) !== null) {
    // Group 1 = dash-form, group 2 = colon-form; exactly one is set per match.
    const name = (match[1] ?? match[2])!.toLowerCase();
    let replacement: TemplateResult | null = null;

    if (EMOTE_SET.has(name)) {
      replacement = html`<img
        class="inline-emote"
        src="/vanilla/ui/emotes/emote_${name}.png"
        alt="${name}"
        title="/${name}"
        loading="lazy"
      />`;
    } else if (name.startsWith('flag') && name.length > 4) {
      const code = name.slice(4);
      const resolved = resolveFlag(code);
      if (resolved) {
        replacement = html`<span class="inline-flag" style=${flagStyle(resolved)} title="${code}"></span>`;
      }
    }

    if (replacement) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(replacement);
      lastIndex = match.index + match[0].length;
    }
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Parsed parts by message text, so rebuilding an unchanged line doesn't re-run
 * linkify and the token regex. Both chats guard their lines, so that's when
 * the game type changes (CTF nick colours) and each time the mobile drawer
 * opens, since its list remounts. Lit reuses cached parts without touching the
 * DOM. Cleared wholesale when full, since it only needs to cover the lines on
 * screen (MAX_CHAT_LINES is 50).
 */
const partsCache = new Map<string, ReadonlyArray<TemplateResult | string>>();
const PARTS_CACHE_MAX = 200;

/** Splits text into plain text, clickable URL segments, and inline emote/flag tokens */
export function renderChatText(text: string): ReadonlyArray<TemplateResult | string> {
  const cached = partsCache.get(text);
  if (cached) return cached;
  const parts = parseChatText(text);
  if (partsCache.size >= PARTS_CACHE_MAX) partsCache.clear();
  partsCache.set(text, parts);
  return parts;
}

function parseChatText(text: string): (TemplateResult | string)[] {
  // First pass: split around URLs (linkify cares about whole string)
  const urlMatches = linkify.match(text) ?? [];

  const parts: (TemplateResult | string)[] = [];
  let lastIndex = 0;

  for (const m of urlMatches) {
    if (m.index > lastIndex) {
      parts.push(...renderInlineTokens(text.substring(lastIndex, m.index)));
    }
    const truncated = truncateUrl(m.url, m.text);
    const needsSrOnly = truncated !== m.text;
    parts.push(
      html`<a target="_blank" rel="noopener noreferrer" href=${m.url} title=${m.text}
        >${needsSrOnly ? html`<span class="sr-only">${m.text}</span>` : nothing}${truncated}</a
      >`,
    );
    lastIndex = m.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(...renderInlineTokens(text.substring(lastIndex)));
  }

  return parts;
}
